/**
 * Caustics Title Pipeline (Phaser 4 filter port).
 *
 * Procedural underwater-caustics overlay that runs INSIDE the letterforms
 * of an image (uses the texture's alpha channel as a mask). Multiple
 * sin-wave layers at different frequencies + speeds drift across the
 * surface to produce the wavy, moving light bands you see refracted on
 * the bottom of a pool.
 *
 * Phaser 4 architecture: paired (RenderNode, Controller). Identical
 * fragment shader to v3 so visual output is preserved.
 *
 * Apply via:
 *   const fx = CausticsTitleFilter.attachTo(titleImage);
 *   fx.setIntensity(0.5);
 *   fx.setSpeed(1.0);
 *   fx.setTint(0.85, 1.0, 1.15);
 *
 * Registration (once, in main.ts game config):
 *   render: { renderNodes: { CausticsTitleShader: { function: CausticsTitleShader } } }
 */

import Phaser from 'phaser';

const CAUSTICS_FRAG = `
#ifdef GL_ES
precision mediump float;
#endif

uniform sampler2D uMainSampler;
uniform float uTime;
uniform float uIntensity;
uniform float uSpeed;
uniform vec3  uTint;

varying vec2 outTexCoord;

float caustics(vec2 uv, float t) {
  float c = 0.0;
  for (int i = 0; i < 3; i++) {
    float fi = float(i) + 1.0;
    vec2 p = uv * (4.0 + fi * 2.5);
    p += vec2(t * 0.35 * fi, t * 0.22 * (fi + 1.0));
    float wave = abs(sin(p.x + sin(p.y * 1.3 + t * 0.5))
                   * cos(p.y + cos(p.x * 0.9 - t * 0.4)));
    c += wave;
  }
  c /= 3.0;
  return pow(c, 2.4);
}

void main() {
  vec4 base = texture2D(uMainSampler, outTexCoord);
  if (base.a < 0.01) {
    gl_FragColor = base;
    return;
  }

  float t = uTime * uSpeed;
  float c = caustics(outTexCoord, t);

  // Solidity mask biases the effect to opaque letter interior; rolls off
  // sharply at antialiased edges so caustics don't halo against the scene.
  float solid = base.a * base.a;

  vec3 boost = uTint * c * uIntensity * solid;
  vec3 lit = base.rgb + boost;

  float peak = smoothstep(0.55, 0.95, c);
  lit = mix(lit, vec3(1.0, 1.0, 1.0), peak * 0.45 * uIntensity * solid);

  gl_FragColor = vec4(lit, base.a);
}
`;

export class CausticsTitleShader extends Phaser.Renderer.WebGL.RenderNodes.BaseFilterShader {
  constructor(manager: Phaser.Renderer.WebGL.RenderNodes.RenderNodeManager) {
    super('CausticsTitleShader', manager, undefined, CAUSTICS_FRAG);
  }

  setupUniforms(
    controller: Phaser.Filters.Controller,
    _drawingContext: Phaser.Renderer.WebGL.DrawingContext,
  ): void {
    const c = controller as CausticsTitleFilter;
    const pm = this.programManager;
    pm.setUniform('uTime', c.camera.scene.game.loop.time / 1000);
    pm.setUniform('uIntensity', c.intensity);
    pm.setUniform('uSpeed', c.speed);
    pm.setUniform('uTint', c.tint);
  }
}

export class CausticsTitleFilter extends Phaser.Filters.Controller {
  intensity = 1.0;
  speed = 1.0;
  tint: [number, number, number] = [0.85, 1.0, 1.15];

  constructor(camera: Phaser.Cameras.Scene2D.Camera) {
    super(camera, 'CausticsTitleShader');
  }

  /** Construct + attach to a game object's internal filter list. */
  static attachTo(
    target: Phaser.GameObjects.GameObject & {
      enableFilters(): unknown;
      filters: Phaser.Types.GameObjects.FiltersInternalExternal | null;
    },
  ): CausticsTitleFilter {
    target.enableFilters();
    const cam = target.filters!.internal.camera;
    const filter = new CausticsTitleFilter(cam);
    target.filters!.internal.add(filter);
    return filter;
  }

  setIntensity(v: number): this { this.intensity = v; return this; }
  setSpeed(v: number): this { this.speed = v; return this; }
  setTint(r: number, g: number, b: number): this { this.tint = [r, g, b]; return this; }
}
